import type { SifaUnterweisung, SifaUnterweisungVorlage } from '../../services/sifa/types';

/**
 * Reine Regeln der Kundenseite der globalen Vorlagen (Nr. 141,
 * docs/sifa-vorlagen-plan.md §3g): Anlegen-Dialog „Leer / Aus Vorlage",
 * Herkunftsmarke und „Neue Fassung" in der Liste, „Als Vorlage speichern".
 *
 * Ohne React und ohne Supabase, damit Suche, Gruppierung und die Texte ohne
 * DOM pruefbar sind (Komponenten sind es hier nicht).
 */

// ─── Leer anlegen ───────────────────────────────────────────────────────────

/** Titel, wenn das Feld leer bleibt — derselbe wie vor dem Dialog. */
export const LEER_TITEL_VORGABE = 'Neue Unterweisung';

/** Ein leeres Titelfeld darf das Anlegen nicht blockieren: der Editor oeffnet ohnehin gleich. */
export const titelOderVorgabe = (titel: string | null | undefined): string =>
  (titel ?? '').trim() || LEER_TITEL_VORGABE;

// ─── Suche ──────────────────────────────────────────────────────────────────

/**
 * Vergleichsform fuer die Suche: klein, ohne Akzente und Umlautpunkte,
 * ß → ss, Leerraum zusammengezogen. Wer am Handy „ubung" tippt, soll
 * „Übung" finden.
 */
export const suchNormal = (text: string | null | undefined): string =>
  (text ?? '')
    .toLocaleLowerCase('de')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();

type Suchfelder = Pick<SifaUnterweisungVorlage, 'titel' | 'kategorie' | 'beschreibung'>;

/**
 * Trifft die Suche? Jedes Wort muss irgendwo in Titel, Kategorie oder
 * Beschreibung stehen — „brand unterweisung" findet „Unterweisung
 * Brandschutz", die Reihenfolge der Woerter spielt keine Rolle.
 */
export const vorlageTrifft = (v: Suchfelder, suche: string | null | undefined): boolean => {
  const woerter = suchNormal(suche).split(' ').filter(Boolean);
  if (woerter.length === 0) return true;
  const heuhaufen = suchNormal([v.titel, v.kategorie, v.beschreibung].filter(Boolean).join(' '));
  return woerter.every((w) => heuhaufen.includes(w));
};

export const filtereVorlagen = <T extends Suchfelder>(vorlagen: readonly T[], suche: string | null | undefined): T[] =>
  vorlagen.filter((v) => vorlageTrifft(v, suche));

// ─── Gruppierung ────────────────────────────────────────────────────────────

/** Ueberschrift fuer Vorlagen ohne Kategorie — steht immer zuletzt. */
export const OHNE_KATEGORIE = 'Ohne Kategorie';

export interface VorlagenGruppe<T> {
  /** Anzeigename der Kategorie, bzw. OHNE_KATEGORIE. */
  kategorie: string;
  vorlagen: T[];
}

const deVergleich = (a: string, b: string): number => a.localeCompare(b, 'de', { sensitivity: 'base' });

/**
 * Nach Kategorie gruppiert, Kategorien und Titel alphabetisch, „Ohne
 * Kategorie" zuletzt. Kategorie ist Freitext: „Brandschutz" und
 * „brandschutz " landen in EINER Gruppe (Schreibweise der ersten bleibt),
 * sonst stuende dieselbe Gruppe zweimal da.
 */
export const gruppiereNachKategorie = <T extends Pick<SifaUnterweisungVorlage, 'id' | 'titel' | 'kategorie'>>(
  vorlagen: readonly T[],
): VorlagenGruppe<T>[] => {
  const gruppen = new Map<string, VorlagenGruppe<T>>();
  for (const v of vorlagen) {
    const name = (v.kategorie ?? '').trim();
    const schluessel = suchNormal(name);
    let g = gruppen.get(schluessel);
    if (!g) {
      g = { kategorie: schluessel ? name : OHNE_KATEGORIE, vorlagen: [] };
      gruppen.set(schluessel, g);
    }
    g.vorlagen.push(v);
  }
  for (const g of gruppen.values()) {
    g.vorlagen.sort((a, b) => deVergleich(a.titel || '', b.titel || '') || a.id.localeCompare(b.id));
  }
  const ohne = gruppen.get('');
  const benannt = [...gruppen.entries()]
    .filter(([schluessel]) => schluessel !== '')
    .map(([, g]) => g)
    .sort((a, b) => deVergleich(a.kategorie, b.kategorie));
  return ohne ? [...benannt, ohne] : benannt;
};

// ─── Kurzinfo ───────────────────────────────────────────────────────────────

export interface VorlageKurzinfo {
  abschnitte: number;
  fragen: number;
  mitVideo: boolean;
  dauerMin: number | null;
  fassung: number;
}

const hatPfad = (p: string | null | undefined): boolean => typeof p === 'string' && p.trim() !== '';

export const zaehleKurzinfo = (
  v: Pick<SifaUnterweisungVorlage, 'inhalt' | 'fragen' | 'dauerMin' | 'fassung'>,
): VorlageKurzinfo => {
  const inhalt = v.inhalt || [];
  const dauer = typeof v.dauerMin === 'number' && Number.isFinite(v.dauerMin) && v.dauerMin > 0 ? v.dauerMin : null;
  return {
    abschnitte: inhalt.length,
    fragen: (v.fragen || []).length,
    mitVideo: inhalt.some((a) => hatPfad(a?.videoPfad)),
    dauerMin: dauer,
    fassung: v.fassung,
  };
};

const zahlWort = (n: number, einzahl: string, mehrzahl: string): string => `${n} ${n === 1 ? einzahl : mehrzahl}`;

/** „3 Abschnitte · 1 Frage · mit Video · ca. 20 min · Fassung 2" — als Teile, die Oberflaeche verbindet sie. */
export const kurzinfoTeile = (k: VorlageKurzinfo): string[] => {
  const teile = [
    zahlWort(k.abschnitte, 'Abschnitt', 'Abschnitte'),
    zahlWort(k.fragen, 'Frage', 'Fragen'),
    k.mitVideo ? 'mit Video' : 'ohne Video',
  ];
  if (k.dauerMin !== null) teile.push(`ca. ${k.dauerMin} min`);
  teile.push(`Fassung ${k.fassung}`);
  return teile;
};

/**
 * Kuerzt eine Beschreibung fuer die Auswahlliste: Leerraum zusammengezogen,
 * am Wortende abgeschnitten, mit „…". Ein Wort, das allein laenger ist,
 * wird hart geschnitten — sonst bliebe nur das „…".
 */
export const kuerze = (text: string | null | undefined, max = 140): string => {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const schnitt = t.slice(0, max);
  const leer = schnitt.lastIndexOf(' ');
  const stueck = leer > max / 2 ? schnitt.slice(0, leer) : schnitt;
  return `${stueck.replace(/[\s,;:.–-]+$/, '')}…`;
};

// ─── Schon im Projekt? ──────────────────────────────────────────────────────

/**
 * vorlageId → Nummern der Unterweisungen DIESES Projekts, die aus ihr
 * stammen (aufsteigend). Auch archivierte zaehlen: sie stehen in der Ablage
 * und sind dieselbe Unterweisung in alter Fassung.
 */
export const nummernJeVorlage = (
  unterweisungen: readonly Pick<SifaUnterweisung, 'vorlageId' | 'nummer'>[],
): Map<string, number[]> => {
  const jeVorlage = new Map<string, number[]>();
  for (const u of unterweisungen) {
    const vid = (u.vorlageId ?? '').trim();
    if (!vid) continue;
    const liste = jeVorlage.get(vid) ?? [];
    if (!liste.includes(u.nummer)) liste.push(u.nummer);
    jeVorlage.set(vid, liste);
  }
  for (const liste of jeVorlage.values()) liste.sort((a, b) => a - b);
  return jeVorlage;
};

/** „bereits angelegt als Nr. 3" bzw. „… als Nr. 3, 5 und 7"; leer, wenn es keine gibt. */
export const bereitsAngelegtText = (nummern: readonly number[] | null | undefined): string => {
  const n = nummern ?? [];
  if (n.length === 0) return '';
  if (n.length === 1) return `bereits angelegt als Nr. ${n[0]}`;
  return `bereits angelegt als Nr. ${n.slice(0, -1).join(', ')} und ${n[n.length - 1]}`;
};

// ─── Liste: Herkunft, neue Fassung, als Vorlage ─────────────────────────────

/** Marke an der Karte; null = selbst angelegt. Ohne bekannte Fassung nur „aus Vorlage". */
export const herkunftText = (u: Pick<SifaUnterweisung, 'vorlageId' | 'vorlageFassung'>): string | null => {
  if (!(u.vorlageId ?? '').trim()) return null;
  const f = u.vorlageFassung;
  return typeof f === 'number' && Number.isFinite(f) ? `aus Vorlage · Fassung ${f}` : 'aus Vorlage';
};

export const neueFassungText = (v: Pick<SifaUnterweisungVorlage, 'fassung'>): string => `Neue Fassung ${v.fassung} verfügbar`;

/** Rueckfrage vor dem Uebernehmen — sagt, was verloren geht und was bleibt. */
export const uebernahmeFrage = (v: Pick<SifaUnterweisungVorlage, 'fassung'>): string =>
  `Die Kundenfassung wird durch Fassung ${v.fassung} der Vorlage ersetzt. ` +
  'Eigene Änderungen an dieser Unterweisung gehen verloren. Link, Nummer und bisherige Teilnahmen bleiben.';

/**
 * „Als Vorlage speichern" nur fuer Admins, Nutzer mit Haekchen „SiFa" und —
 * Rollensystem — wer das Recht „SiFa-Vorlagen" hat (ODER neben dem Haekchen).
 * Anzeige, kein Riegel — die Policies aus Nr. 141 und Nr. 154 pruefen dasselbe.
 */
export const darfAlsVorlageSpeichern = (rolle: unknown, istSifa: boolean, hatRecht = false): boolean =>
  rolle === 'admin' || rolle === 'super_admin' || istSifa || hatRecht;

/** Erfolgsmeldung samt Hinweisen des Service (je Hinweis eine Zeile). */
export const alsVorlageMeldung = (titel: string, hinweise: readonly string[] = []): string => {
  const kopf = `Vorlage „${(titel || '').trim() || 'ohne Titel'}" als Entwurf angelegt — im Menü Unterweisungen prüfen und freigeben.`;
  const rest = hinweise.map((h) => (h || '').trim()).filter(Boolean);
  return rest.length > 0 ? [kopf, 'Hinweise:', ...rest].join('\n') : kopf;
};
